import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PatientPicker from '@components/Patient/PatientPicker';
import {
  describeFilter,
  FilterBar,
  type FilterFieldDef,
  type FiltersState,
  type FilterValue,
  SortMenuButton,
} from '@components/ui/FilterMenu';
import { useClinicians } from '@features/organizations';
import { mapPatientInfoResponseToPatient, usePatientInfoQuery } from '@features/patients';
import { useSubjectLanguageVariants } from '@features/user';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getAppScrollTop, scrollAppTo } from '@utils/appScroller';
import { useSnackbar } from 'notistack';

import type { Patient, PatientInfo } from '../../../../../types/types';
import type {
  BillingSavedView,
  ClaimWorkQueueCounts,
  ClaimWorkQueueItem,
  ClaimWorkSavedConfig,
  ClaimWorkSortKey,
} from '../../../store';
import {
  selectShowHiddenClaimRows,
  setShowHiddenClaimRows,
  useBatchClaimSubmit,
  useClaimWorkPageQuery,
  useClaimWorkSavedViewsQuery,
  useCreateClaimWorkSavedViewMutation,
  useDeleteClaimWorkSavedViewMutation,
  useUpdateClaimWorkSavedViewMutation,
} from '../../../store';
import type { WorkViewKey as ClaimWorkViewKey } from '../../../types/encounterWork';
import { isQueueViewKey } from '../../../types/encounterWork';
import { formatDateOnly } from '../../../utils';
import {
  isWorkableClaimStage,
  WORK_STATE_COLORS,
  workStateChipLabel,
  workStateChipVariant,
} from '../../../utils/claimWorkPresentation';
import { readWorklistReturn, workbenchProvenanceState } from '../../../utils/worklistProvenance';
import { jotBillStickyTop } from '../jotBillHeaderHeight';
import {
  type ColumnDef,
  DataTable,
  DispositionChip,
  formatCents,
  Pagination,
  PatientIdentity,
  ProviderIdentity,
  type SelectionAction,
  useBillingPayerOptionsQuery,
  useColumnPreferences,
} from '../shared';
import {
  HOVER_DETAIL_TRIGGER_SX,
  HoverDetail,
  IssueDetailContainer,
  ReasonCell,
  ReasonDetailContainer,
} from '../work';

import { deriveClaimBatchRowStates } from './claimBatchRowStates';
import { ClaimBulkActionSlot } from './ClaimBulkActionSlot';
import { ClaimGroupMenuButton } from './ClaimGroupMenuButton';
import { ClaimQueueRowMenu } from './ClaimQueueRowMenu';
import { type ClaimRowGroupBy, claimRowGroupHeader, groupClaimRows } from './claimRowGrouping';
import { selectNewerClaimWorkCounts } from './claimWorkCounts';
import {
  readClaimWorkEphemera,
  updateClaimWorkEphemeraScroll,
  writeClaimWorkEphemera,
} from './claimWorkEphemera';
import {
  createDefaultClaimWorkPredicates,
  dateRangeForWindow,
  RELATIVE_DATE_WINDOWS,
  savedViewVisibleColumns,
} from './claimWorkFilters';
import {
  applyClaimWorkUrlState,
  claimWorkStateSignature,
  type ClaimWorkUrlState,
  parseClaimWorkUrlState,
  railViewKey,
} from './claimWorkUrlState';
import type { StoredClaimWorkViewKey } from './claimWorkViewGroups';
import {
  claimWorkViewLabel,
  claimWorkViewLabelForCount,
  DEFAULT_CLAIM_WORK_VIEW,
  isStoredClaimWorkViewKey,
  resolveClaimWorkViewKey,
} from './claimWorkViewGroups';
import { ClaimWorkViewRail } from './ClaimWorkViewRail';
import { ShowHiddenRowsToggle } from './ShowHiddenRowsToggle';
import { useWorklistRowPrefetch } from './useWorklistRowPrefetch';

const selectedValues = (predicates: FiltersState, fieldId: string): string[] => {
  const value = predicates[fieldId];
  return value?.kind === 'multiSelect' ? value.values : [];
};

const selectedDateRange = (
  predicates: FiltersState,
): Extract<FilterValue, { kind: 'dateRange' }> | undefined => {
  const value = predicates.serviceDate;
  return value?.kind === 'dateRange' ? value : undefined;
};

const commaSeparated = (values: string[]): string | undefined =>
  values.length > 0 ? values.join(',') : undefined;

const isPatientInfo = (patient: Patient | PatientInfo): patient is PatientInfo => 'name' in patient;

const SORTS: Array<{ key: ClaimWorkSortKey; label: string }> = [
  { key: 'service_date_desc', label: 'Service date · newest' },
  { key: 'service_date_asc', label: 'Service date · oldest' },
  { key: 'charge_desc', label: 'Charge · highest' },
  { key: 'charge_asc', label: 'Charge · lowest' },
];

const CLAIM_LIFECYCLES = [
  'draft',
  'processing',
  'ready',
  'submitted',
  'acknowledged',
  'accepted',
  'rejected',
  'denied',
  'paid',
  'partially_paid',
  'reversed',
];

const formatDate = (value?: string | null): string =>
  formatDateOnly(value, { day: 'numeric', month: 'short' }, '—');

/** "Claims to fix" reads as "3 claims to fix" mid-sentence. */
const lowerFirst = (value: string): string => value.charAt(0).toLowerCase() + value.slice(1);

const MS_PER_DAY = 86_400_000;

/**
 * Whole days since the payer last said anything, for the Days column on
 * Claims to fix. Blank when there is no payer event to count from: an
 * invented zero would read as "answered today".
 */
const daysSince = (value?: string | null): string => {
  if (!value) return '';
  const occurred = Date.parse(value);
  if (!Number.isFinite(occurred)) return '';
  const days = Math.floor((Date.now() - occurred) / MS_PER_DAY);
  return days >= 0 ? String(days) : '';
};

/**
 * The saved-view config as this tab builds it. Identical to the generated
 * shape except that `work_view` may carry a queue key the pre-queue OpenAPI
 * document does not know yet; one documented cast at each mutation boundary
 * until packet F regenerates the client.
 */
type ClaimWorkQueueSavedConfig = Omit<ClaimWorkSavedConfig, 'work_view'> & {
  /* Stored, aliases included: a view saved on `needs_correction` keeps that
     key on every write, so Update never narrows it to `fix`. */
  work_view?: StoredClaimWorkViewKey;
};

/**
 * Two configs are the same list when they ASK FOR the same thing. The work
 * view is compared through its rail key, so a saved view stored on
 * `needs_correction` and a tab showing Fix are not called different, which is
 * what put "Unsaved changes" on every such view the moment it opened, one
 * Update away from silently narrowing it.
 */
const normalizeConfig = (config: ClaimWorkQueueSavedConfig | ClaimWorkSavedConfig): string =>
  JSON.stringify({
    filters: {
      claim_lifecycles: config.filters?.claim_lifecycles ?? [],
      patient_ids: config.filters?.patient_ids ?? [],
      payer_ids: config.filters?.payer_ids ?? [],
      provider_ids: config.filters?.provider_ids ?? [],
      service_date_window_days: config.filters?.service_date_window_days ?? null,
      service_date_from: config.filters?.service_date_from ?? null,
      service_date_to: config.filters?.service_date_to ?? null,
    },
    sort: config.sort ?? 'service_date_desc',
    visible_columns: config.visible_columns ?? [],
    work_view: resolveClaimWorkViewKey(config.work_view) ?? DEFAULT_CLAIM_WORK_VIEW,
  });

const first = (values?: string[]): string => values?.[0] ?? '';

/* Stable empty stack for the derived page state — a fresh [] every render
   would churn the ephemera-persistence effect's deps. */
const EMPTY_CURSOR_HISTORY: Array<string | undefined> = [];

const filtersFromConfig = (config: ClaimWorkSavedConfig): FiltersState => {
  const predicates: FiltersState = {};
  const addMultiSelect = (fieldId: string, values?: string[]) => {
    if (values?.length) predicates[fieldId] = { kind: 'multiSelect', values };
  };
  addMultiSelect('claimLifecycle', config.filters?.claim_lifecycles);
  addMultiSelect('patient', config.filters?.patient_ids);
  addMultiSelect('payer', config.filters?.payer_ids);
  addMultiSelect('provider', config.filters?.provider_ids);

  if (config.filters?.service_date_window_days) {
    predicates.serviceDate = dateRangeForWindow(config.filters.service_date_window_days);
  } else if (config.filters?.service_date_from || config.filters?.service_date_to) {
    predicates.serviceDate = {
      kind: 'dateRange',
      start: config.filters.service_date_from ?? null,
      end: config.filters.service_date_to ?? null,
    };
  }
  return predicates;
};

const getDefaultColumns = (subjectLabel: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', required: true, width: '82px' },
  { key: 'patient_name', label: subjectLabel, required: true, type: 'patient', width: '1.1fr' },
  { key: 'provider_name', label: 'Provider', width: '1.2fr' },
  { key: 'payer_name', label: 'Payer', width: '0.9fr' },
  { key: 'issues', label: 'Issues and findings', required: true, width: '1.8fr' },
  { key: 'amount', label: 'Charge', required: true, type: 'mono', align: 'right', width: '90px' },
  { key: 'work_state', label: 'State', required: true, width: '132px' },
];

/**
 * Claims to fix asks a different question than the rest of the list. The
 * biller is not deciding whether to send a claim; the payer already answered,
 * and the work is reading that answer. So the pre-flight advice column
 * (`issues`) goes, and what the payer said arrives in its place: the outcome,
 * the reason codes over the category they normalize to, how long the claim
 * has been sitting on that answer, and the claim number a payer phone call
 * needs (GDN-3381).
 */
/* GDN-3383 added a tenth column, so the fixed widths went on a diet: the
   columns holding one short token (Claim #, Outcome, Charge, Days) each gave
   up a few pixels they were not using, and the two columns that carry
   SENTENCES — Reason and Payer claim # — took the room. A number never
   truncates; a caption does, and a truncated caption is the one that costs
   a biller a phone call. */
const getFixColumns = (subjectLabel: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', required: true, width: '82px' },
  { key: 'patient_name', label: subjectLabel, required: true, type: 'patient', width: '1.3fr' },
  /* No `type: 'mono'`: the cell renderer owns this column, because only a
     REAL claim number may wear the mono treatment. */
  { key: 'claim_number', label: 'Claim #', width: '88px' },
  /* No Provider here, unlike every other view. Nobody calls the rendering
     clinician about a denial; the payer, the codes and the days are the
     conversation, and on a table this wide the column that earns its place
     last is the one that squeezes the ones that do. */
  { key: 'payer_name', label: 'Payer', width: '0.7fr' },
  { key: 'outcome', label: 'Outcome', required: true, width: '92px' },
  { key: 'reason', label: 'Reason', required: true, width: '1.5fr' },
  /* The PAYER's number for this claim, not ours (GDN-3383). It is what a
     payer's phone system asks for first, and on a rejected claim its absence
     is itself the answer: nothing reached the payer, so nothing has a payer
     number yet.
     The floor is what the VALUE needs ("No payer claim #"), not what the
     caption wants. It was 150px, which is what "Clearinghouse rejection"
     reads at; that floor plus the disposition column's own 150px floor
     starved the patient name down to 62px. Between a caption that
     ellipsizes and a patient nobody can identify, the caption yields. See
     the column-budget note in the PR: at eleven columns this view is over
     budget and the fix is a product decision, not another few pixels. */
  { key: 'payer_reference', label: 'Payer claim #', width: 'minmax(108px, 0.9fr)' },
  { key: 'amount', label: 'Charge', required: true, type: 'mono', align: 'right', width: '78px' },
  /* Mono so the numbers line up, and so an unanswered claim leaves the cell
     BLANK: the default cell type fills an empty value with a dash. */
  { key: 'days', label: 'Days', align: 'right', type: 'mono', width: '44px' },
];

/** The disposition column is driven by the toggle, not by column
 *  preferences: it exists to explain rows the toggle just revealed. */
const DISPOSITION_COLUMN: ColumnDef = {
  key: 'disposition',
  label: 'Disposition',
  /* The floor is what "until Sep 10 · M. Lund" needs. Below it the caption
     truncates and drops the one word that says WHO decided, and a chip that
     names nobody invites the biller to override it. */
  width: 'minmax(150px, 1fr)',
};

export const ClaimsTab: React.FC = () => {
  const { plural, singularCap } = useSubjectLanguageVariants();
  const { billing } = useEventTracking();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  /* Per clinician, through the billing feature's user-scoped redux persist:
     practices share machines, and one biller's choice to work with hidden
     rows on screen must not follow the next person who signs in. */
  const showHiddenPreference = useAppSelector(selectShowHiddenClaimRows);
  const { clinicians, isLoading: cliniciansLoading } = useClinicians();
  const { data: payerData, isLoading: payersLoading } = useBillingPayerOptionsQuery();
  /* GDN-3196: THE URL IS THE LIST. Canonical state — work view, saved view,
     filters, sort, per-page — parses out of the query string every render,
     and every change writes back via REPLACE: reload-safe and shareable by
     construction, with zero history spam. `useState` below holds only the
     deliberately-ephemeral rest — and row selection stays out of BOTH on
     purpose: restoring a checked batch after time away invites submitting a
     selection whose members changed underneath you. */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(() => parseClaimWorkUrlState(searchParams), [searchParams]);
  const {
    groupBy: urlGroupBy,
    perPage,
    predicates,
    savedViewId: activeSavedViewId,
    showHidden,
    sort,
    workView,
  } = urlState;
  /* `workView` is the key AS STORED and is what the server is asked for;
     `railView` is the same key resolved for the UI. They differ exactly when
     an old link or a saved view carries `needs_correction`, which the server
     reads as the union of review and fix: the rail lights Fix, and the rows
     stay the union the view was saved to hold. */
  const railView = railViewKey(workView);
  /* Grouping is offered on Claims to fix and nowhere else (GDN-3383): it
     answers "which reason is costing me the most claims", and only the queue
     where a payer has already answered can ask that. Keyed off railView, so
     a saved `needs_correction` view — the one the rail lights as Fix — gets
     the control too. A `group` param on any other view is inert rather than
     an error: a wrong URL still yields a right list. */
  const canGroup = railView === 'fix';
  const groupBy: ClaimRowGroupBy = canGroup ? urlGroupBy : 'none';
  /* Only a queue can hide a row (contract §1), so the flag is inert on a
     list: no toggle, no `include_hidden`, no disposition-driven filtering. */
  const isQueueView = isQueueViewKey(railView);
  const includeHidden = isQueueView && showHidden;
  const writeUrlState = useCallback(
    (change: Partial<ClaimWorkUrlState>) => {
      setSearchParams(
        prev => applyClaimWorkUrlState(prev, { ...parseClaimWorkUrlState(prev), ...change }),
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const stateSignature = useMemo(() => claimWorkStateSignature(urlState), [urlState]);

  const [selectedPatient, setSelectedPatient] = useState<Patient | PatientInfo>();
  /* Cursor stack hydrates from sessionStorage when the stored record belongs
     to THIS canonical state (best-effort — cursors are server-opaque). The
     page state REMEMBERS which signature it was built under: if the URL
     changes underneath it (browser back/forward, a pasted link), the
     mismatch derives to page 1 in the same render — a cursor never rides
     into a query it doesn't belong to. */
  const [restoredEphemera] = useState(() => readClaimWorkEphemera(stateSignature));
  const [pageState, setPageState] = useState<{
    signature: string;
    cursor: string | undefined;
    history: Array<string | undefined>;
  }>(() => ({
    signature: stateSignature,
    cursor: restoredEphemera?.cursor,
    history: restoredEphemera?.cursorHistory ?? [],
  }));
  const cursorStateCurrent = pageState.signature === stateSignature;
  const cursor = cursorStateCurrent ? pageState.cursor : undefined;
  const cursorHistory = cursorStateCurrent ? pageState.history : EMPTY_CURSOR_HISTORY;
  /* True only while a restored cursor is unconfirmed: if the server rejects
     it (expired token), fall back to page 1 of the same filtered list —
     never an error, never a loop. Cleared by the first success or the first
     user-driven page change. */
  const restoredCursorRef = useRef(Boolean(restoredEphemera?.cursor));
  const scrollRestoredRef = useRef(false);
  /* The return anchor (GDN-3197): captured from history state ONCE at mount,
     then stripped, so a later refresh doesn't re-flash a row the biller
     already found. Flash when the worked claim is still on this page;
     honest note when it left the view. */
  const location = useLocation();
  const [returnAnchor] = useState(() => readWorklistReturn(location.state));
  const [flashClaimId, setFlashClaimId] = useState<string | null>(null);
  const [leftViewNote, setLeftViewNote] = useState<string | null>(null);
  const returnConsumedRef = useRef(false);
  /* Mount barrier for the anchor: the mount refetch is DISPATCHED in the
     effects phase after the first committed render, so an effect running in
     that same flush still holds a closure where isFetching is false — and
     would consume a 15s+-stale cache before the in-flight state ever
     renders. State (not a ref) so a second render is guaranteed even when
     no refetch fires; from that render on, the refetch decision is
     visible. */
  const [anchorArmed, setAnchorArmed] = useState(false);
  useEffect(() => {
    setAnchorArmed(true);
  }, []);
  /* The remembered preference seeds the URL only where the URL is SILENT.
     The address stays the whole truth about the list (GDN-3196): a link that
     carries `hidden=1` (packet C's empty state) says so itself, and a
     clinician who turned the toggle off leaves both the param and the
     preference off, so this never fires behind them. */
  const urlStatesHidden = searchParams.has('hidden');
  useEffect(() => {
    if (urlStatesHidden || !showHiddenPreference || !isQueueView) return;
    writeUrlState({ showHidden: true });
  }, [isQueueView, showHiddenPreference, urlStatesHidden, writeUrlState]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  /* A saved view's columns, waiting for the URL to reach the view they
     belong to (see applySavedView). */
  const [pendingSavedViewColumns, setPendingSavedViewColumns] = useState<{
    columns: string[];
    savedViewId: string;
  } | null>(null);
  const [saveName, setSaveName] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [railCounts, setRailCounts] = useState<ClaimWorkQueueCounts>();

  const defaultColumns = useMemo(
    () => (railView === 'fix' ? getFixColumns(singularCap) : getDefaultColumns(singularCap)),
    [railView, singularCap],
  );
  /* Keyed per view: Claims to fix asks for a different column set than the
     rest of the list, and one shared key let a saved view's Fix columns
     follow the biller back to a queue where they mean nothing. */
  const { allColumns, updatePreferences, visibleColumns, visibleKeys } = useColumnPreferences(
    `claim-work-${railView}`,
    defaultColumns,
  );

  const serviceDate = selectedDateRange(predicates);
  const serviceDateWindowDays =
    serviceDate?.windowDays ??
    (serviceDate?.preset ? RELATIVE_DATE_WINDOWS[serviceDate.preset] : undefined);
  const currentConfig = useMemo<ClaimWorkQueueSavedConfig>(
    () => ({
      filters: {
        claim_lifecycles: selectedValues(predicates, 'claimLifecycle'),
        patient_ids: selectedValues(predicates, 'patient'),
        payer_ids: selectedValues(predicates, 'payer'),
        provider_ids: selectedValues(predicates, 'provider'),
        service_date_window_days: serviceDateWindowDays ?? null,
        service_date_from: serviceDateWindowDays ? null : (serviceDate?.start ?? null),
        service_date_to: serviceDateWindowDays ? null : (serviceDate?.end ?? null),
      },
      sort,
      visible_columns: visibleKeys,
      work_view: workView,
    }),
    [predicates, serviceDate, serviceDateWindowDays, sort, visibleKeys, workView],
  );

  const filterArgs = useMemo(
    () => ({
      claimLifecycles: commaSeparated(selectedValues(predicates, 'claimLifecycle')),
      patientIds: commaSeparated(selectedValues(predicates, 'patient')),
      payerIds: commaSeparated(selectedValues(predicates, 'payer')),
      providerIds: commaSeparated(selectedValues(predicates, 'provider')),
      serviceDateFrom: serviceDateWindowDays ? undefined : (serviceDate?.start ?? undefined),
      serviceDateTo: serviceDateWindowDays ? undefined : (serviceDate?.end ?? undefined),
      serviceDateWindowDays,
      sort,
      workView,
    }),
    [predicates, serviceDate, serviceDateWindowDays, sort, workView],
  );

  const queryArgs = useMemo(
    () => ({ ...filterArgs, cursor, includeHidden, limit: perPage }),
    [cursor, filterArgs, includeHidden, perPage],
  );

  const { currentData, data, error, isFetching, isLoading, refetch } = useClaimWorkPageQuery(
    queryArgs,
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: 15,
      refetchOnReconnect: true,
    },
  );

  /* The rail's counts must not move when the toggle does: a queue's number is
     the work still outstanding, and "4" turning into "7" because someone
     looked at what they had already archived would be a lie about the
     backlog. So while hidden rows are on screen the counts come from their
     own hidden-EXCLUDED read; with the toggle off the page's own counts
     already are that, and this second request never fires. */
  const railCountArgs = useMemo(() => ({ ...filterArgs, limit: 1 }), [filterArgs]);
  const { data: hiddenExcludedCounts } = useClaimWorkPageQuery(railCountArgs, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: 15,
    refetchOnReconnect: true,
    skip: !includeHidden,
  });
  /* `currentData`, not `data`: RTK Query's `data` holds the last result for
     ANY args, so the instant the toggle goes off it is still the
     hidden-INCLUDED page, and those counts would reach the rail for one round
     trip. `currentData` is undefined until the read for THESE args lands, and
     the counts below are all sticky or pending-aware, so the rail simply
     holds its last true number instead of flashing a wrong one. */
  const countsSource = includeHidden ? hiddenExcludedCounts : currentData;
  /* The regenerated client gives this route an argument object (the server
     added an optional `include_hidden` to the saved-view counts). Empty is
     the same request as before: saved-view counts stay hidden-excluded, like
     the rail's. */
  const { data: savedViewData } = useClaimWorkSavedViewsQuery({});
  const [createSavedView, createState] = useCreateClaimWorkSavedViewMutation();
  const [updateSavedView, updateState] = useUpdateClaimWorkSavedViewMutation();
  const [deleteSavedView, deleteState] = useDeleteClaimWorkSavedViewMutation();
  const {
    batchProgress,
    dismissBatch,
    isBatchActive,
    isBatchSubmitting,
    isBatchTerminal,
    submitBatch,
  } = useBatchClaimSubmit();
  /* Server truth per batched claim (queued/submitting/submitted/attention) —
     rows render from this; the work-state chip is never overwritten. */
  const batchRowStates = useMemo(
    () => deriveClaimBatchRowStates(batchProgress?.items),
    [batchProgress],
  );

  const savedViews = savedViewData?.views ?? [];
  const activeSavedView = savedViews.find(view => view.billing_saved_view_id === activeSavedViewId);
  /* Name hydration for the patient chip, keyed off the PREDICATE rather than
     the saved view: with the patient id living in the URL (GDN-3196), a
     reload or shared link restores the filter with no picker interaction
     behind it — the chip must still get a name, from any source of the id. */
  const urlPatientId = first(selectedValues(predicates, 'patient'));
  const { data: urlPatientData } = usePatientInfoQuery(
    { patientId: urlPatientId },
    { skip: !urlPatientId },
  );
  const dirty = Boolean(
    activeSavedView && normalizeConfig(activeSavedView.config) !== normalizeConfig(currentConfig),
  );
  const isSavedViewMutating =
    createState.isLoading || updateState.isLoading || deleteState.isLoading;

  useEffect(() => {
    setRailCounts(current => selectNewerClaimWorkCounts(current, countsSource?.counts));
  }, [countsSource?.counts]);

  useEffect(() => {
    if (!urlPatientId) {
      setSelectedPatient(current => (current ? undefined : current));
      return;
    }
    setSelectedPatient(current => {
      /* A picker-selected object for the same patient is already right —
         keep it rather than churning identity every fetch. */
      if (current && 'patient_id' in current && current.patient_id === urlPatientId) return current;
      if (urlPatientData) return mapPatientInfoResponseToPatient(urlPatientData, urlPatientId);
      /* The URL names a DIFFERENT patient than the chip shows and the new
         name hasn't arrived (pending, or the fetch failed) — clear NOW. A
         generic chip label while loading is honest; the old patient's name
         over a list filtered by the new id is not. */
      return current ? undefined : current;
    });
  }, [urlPatientData, urlPatientId]);

  useEffect(() => {
    if (!isBatchTerminal) return;
    refetch();
  }, [isBatchTerminal, refetch]);

  /* Ephemera persistence (GDN-3196): the cursor stack rides sessionStorage
     under the canonical-state signature; scroll is captured at leave time.
     Both best-effort by design. The derived cursor/cursorHistory are what's
     written, so a signature change persists page 1 for the NEW state rather
     than pairing the old stack with the new signature. */
  useEffect(() => {
    writeClaimWorkEphemera({ signature: stateSignature, cursor, cursorHistory });
  }, [cursor, cursorHistory, stateSignature]);
  /* A LAYOUT-effect cleanup, deliberately. React runs it inside the commit's
     mutation phase, before this subtree's DOM is detached and before the
     incoming route's DOM is inserted — so it reads the offset the biller
     actually left at. A passive cleanup runs after the swap, by which time
     the workbench's first render (a short skeleton) has already clamped the
     scroller to 0, and 0 is what got stored — every time on Firefox, and on
     any browser whenever the incoming page starts out shorter than the
     offset. */
  useLayoutEffect(
    () => () => {
      updateClaimWorkEphemeraScroll(getAppScrollTop());
    },
    [],
  );
  useEffect(() => {
    if (scrollRestoredRef.current || !data) return;
    scrollRestoredRef.current = true;
    /* Through the app-scroller helper: the page scrolls inside RootView's
       container, not the window — window.scrollY reads ~0 here, which made
       this restore a silent no-op until GDN-3220. */
    if (restoredEphemera?.scrollY) scrollAppTo(restoredEphemera.scrollY);
  }, [data, restoredEphemera]);

  /* The restored cursor is confirmed by its first successful read — and if
     the server rejects it instead (expired token), the fallback is page 1 of
     the SAME filtered list, silently. Only a RESTORED cursor gets this
     treatment: a mid-session pagination error keeps the existing loud
     retry alert. */
  useEffect(() => {
    if (data) restoredCursorRef.current = false;
  }, [data]);
  useEffect(() => {
    if (!error || !restoredCursorRef.current) return;
    restoredCursorRef.current = false;
    setPageState({ signature: stateSignature, cursor: undefined, history: [] });
  }, [error, stateSignature]);

  const selectedPatientName = selectedPatient
    ? isPatientInfo(selectedPatient)
      ? (selectedPatient.name ?? '')
      : `${selectedPatient.first_name ?? ''} ${selectedPatient.last_name ?? ''}`.trim()
    : '';
  const filterFields = useMemo<FilterFieldDef[]>(
    () => [
      {
        icon: <LocalHospitalOutlinedIcon fontSize="inherit" />,
        id: 'provider',
        label: 'Provider',
        loading: cliniciansLoading,
        options: clinicians.map(clinician => ({ label: clinician.name, value: clinician.userId })),
        type: 'multiSelect',
      },
      {
        icon: <VerifiedUserOutlinedIcon fontSize="inherit" />,
        id: 'payer',
        label: 'Payer',
        loading: payersLoading,
        options: (payerData?.payers ?? []).map(payer => ({
          label: payer.payer_name,
          value: payer.payer_uuid,
        })),
        type: 'multiSelect',
      },
      {
        describeValue: value => {
          /* A saved view can carry several patient ids; the chip must say
             so rather than naming only the first while the list shows
             claims for all of them. The single-id fallback is the subject
             label, never the raw id. */
          const idCount = value.kind === 'multiSelect' ? value.values.length : 0;
          if (idCount > 1) return { op: 'is', value: `${idCount} ${plural}` };
          return { op: 'is', value: selectedPatientName || singularCap };
        },
        icon: <PersonOutlineOutlinedIcon fontSize="inherit" />,
        id: 'patient',
        label: singularCap,
        renderEditor: ({ autoFocus, onChange, onClose }) => (
          <Box sx={{ p: 1.5 }}>
            <PatientPicker
              autoFocus={autoFocus}
              disableAddNew
              initialPatient={selectedPatient}
              openOnFocus
              onPatientSelect={(patient: Patient | undefined) => {
                setSelectedPatient(patient);
                onChange(patient ? { kind: 'multiSelect', values: [patient.patient_id] } : null);
                if (patient) onClose();
              }}
              size="small"
            />
          </Box>
        ),
        type: 'custom',
      },
      {
        icon: <ReceiptLongOutlinedIcon fontSize="inherit" />,
        id: 'claimLifecycle',
        label: 'Claim status',
        options: CLAIM_LIFECYCLES.map(status => ({
          label: status
            .replaceAll('_', ' ')
            .replace(/^./, firstCharacter => firstCharacter.toUpperCase()),
          value: status,
        })),
        type: 'multiSelect',
      },
      {
        icon: <CalendarTodayOutlinedIcon fontSize="inherit" />,
        id: 'serviceDate',
        label: 'Service date',
        type: 'dateRange',
      },
    ],
    [
      clinicians,
      cliniciansLoading,
      payerData?.payers,
      payersLoading,
      plural,
      selectedPatient,
      selectedPatientName,
      singularCap,
    ],
  );

  /* Page 1 of whatever canonical state the pending URL write lands on — the
     signature recomputes on that render, so an explicitly-reset page state
     stays derived-valid either way. */
  const resetPage = useCallback(() => {
    restoredCursorRef.current = false;
    setPageState(current => ({ ...current, cursor: undefined, history: [] }));
    setSelectedIds(new Set());
  }, []);

  const updatePredicates = useCallback(
    (next: FiltersState) => {
      /* One event per field whose predicate actually changed — filter ids
         are a closed set (provider/payer/patient/claimLifecycle/serviceDate),
         values never leave the client. */
      const fieldIds = new Set([...Object.keys(predicates), ...Object.keys(next)]);
      for (const fieldId of fieldIds) {
        if (JSON.stringify(predicates[fieldId]) !== JSON.stringify(next[fieldId])) {
          billing.claimWorklistFilterChanged({ field_id: fieldId });
        }
      }
      writeUrlState({ predicates: next });
      if (selectedValues(next, 'patient').length === 0) setSelectedPatient(undefined);
      resetPage();
    },
    [billing, predicates, resetPage, writeUrlState],
  );

  const clearPredicates = useCallback(() => {
    billing.claimWorklistFilterChanged({ field_id: 'clear_all' });
    writeUrlState({ predicates: {} });
    setSelectedPatient(undefined);
    resetPage();
  }, [billing, resetPage, writeUrlState]);

  const resetPredicates = useCallback(() => {
    billing.claimWorklistFilterChanged({ field_id: 'reset' });
    writeUrlState({ predicates: createDefaultClaimWorkPredicates() });
    setSelectedPatient(undefined);
    resetPage();
  }, [billing, resetPage, writeUrlState]);

  const selectWorkView = useCallback(
    (value: ClaimWorkViewKey) => {
      billing.claimWorklistWorkViewSelected({ work_view: value });
      /* ONE URL write per interaction — the handlers never stack
         setSearchParams calls, so the replace discipline holds and no write
         can read another's stale params. */
      writeUrlState({
        workView: value,
        savedViewId: null,
        /* Grouping needs no reset here: the serializer drops `group` on any
           view but Claims to fix, so leaving this queue leaves the banding
           behind by construction. */
        ...(activeSavedViewId
          ? { predicates: createDefaultClaimWorkPredicates(), sort: 'service_date_desc' as const }
          : {}),
      });
      if (activeSavedViewId) setSelectedPatient(undefined);
      resetPage();
    },
    [activeSavedViewId, billing, resetPage, writeUrlState],
  );

  const toggleHiddenRows = useCallback(
    (next: boolean) => {
      billing.hiddenRowsToggled({ on: next, work_view: railView });
      /* Both, deliberately: the URL so this exact list is shareable and
         reload-safe (GDN-3196), and the per-clinician preference so the next
         visit opens the way this biller works. */
      dispatch(setShowHiddenClaimRows(next));
      writeUrlState({ showHidden: next });
      resetPage();
    },
    [billing, dispatch, railView, resetPage, writeUrlState],
  );

  const changeGrouping = useCallback(
    (next: ClaimRowGroupBy) => {
      billing.claimWorklistGroupingChanged({ group_by: next, work_view: railView });
      /* The URL only. No `resetPage()`: grouping reorders the page the
         biller is already looking at, and throwing them back to page 1 for
         a rendering choice would lose their place for nothing. Selection
         survives for the same reason: the same rows are on screen. */
      writeUrlState({ groupBy: next });
    },
    [billing, railView, writeUrlState],
  );

  const applySavedView = useCallback(
    (view: BillingSavedView) => {
      billing.claimWorklistSavedViewSelected({ saved_view_id: view.billing_saved_view_id });
      writeUrlState({
        savedViewId: view.billing_saved_view_id,
        /* Stored key, verbatim. Rewriting `needs_correction` to `fix` here
           would narrow the org's view to half its rows the moment anyone
           opened it, and mark it dirty for having done so. */
        workView: isStoredClaimWorkViewKey(view.config.work_view)
          ? view.config.work_view
          : DEFAULT_CLAIM_WORK_VIEW,
        sort: view.config.sort ?? 'service_date_desc',
        predicates: filtersFromConfig(view.config),
      });
      /* Columns are part of the saved view. Leaving the local column
         preferences active marked a freshly opened view dirty and let
         Update overwrite the organization's saved columns with the local
         set. (The patient chip's name hydrates from the predicate effect —
         same path as a reload.)
         The write is DEFERRED, because the column preferences are keyed by
         view and this handler still holds the previous view's key and
         column set: writing here filed the incoming view's columns under
         the view the biller just left. The effect below runs once the URL
         has landed on the new view. */
      setPendingSavedViewColumns({
        columns: view.config.visible_columns ?? [],
        savedViewId: view.billing_saved_view_id,
      });
      resetPage();
    },
    [billing, resetPage, writeUrlState],
  );

  /* Keyed on the saved view actually being active: that is the signal the URL
     write has landed, so `allColumns` is now the incoming view's set and
     `savedViewVisibleColumns` filters against the right one. */
  useEffect(() => {
    if (!pendingSavedViewColumns) return;
    if (activeSavedViewId !== pendingSavedViewColumns.savedViewId) return;
    const nextColumns = savedViewVisibleColumns(pendingSavedViewColumns.columns, allColumns);
    if (nextColumns) updatePreferences(nextColumns, allColumns);
    setPendingSavedViewColumns(null);
  }, [activeSavedViewId, allColumns, pendingSavedViewColumns, updatePreferences]);

  const saveCurrentView = useCallback(async () => {
    try {
      const created = await createSavedView({
        createBillingSavedViewRequest: {
          /* The queue keys are real to the server (contract §1) and unknown
             to the pre-queue OpenAPI document the client is generated from.
             One cast, here and at the update below, until packet F. */
          config: currentConfig,
          definition_version: 1,
          name: saveName.trim(),
        },
      }).unwrap();
      billing.claimWorklistSavedViewSubmitted({
        saved_view_id: created.billing_saved_view_id,
        action: 'create',
      });
      writeUrlState({ savedViewId: created.billing_saved_view_id });
      setSaveDialogOpen(false);
      setSaveName('');
      enqueueSnackbar(`Saved “${created.name}” for this organization.`, { variant: 'success' });
    } catch {
      enqueueSnackbar('The view could not be saved. Refresh the list and try again.', {
        variant: 'error',
      });
    }
  }, [billing, createSavedView, currentConfig, enqueueSnackbar, saveName, writeUrlState]);

  const updateCurrentView = useCallback(async () => {
    if (!activeSavedView) return;
    try {
      await updateSavedView({
        savedViewId: activeSavedView.billing_saved_view_id,
        updateBillingSavedViewRequest: {
          config: currentConfig,
          definition_version: activeSavedView.definition_version ?? 1,
          expected_version: activeSavedView.version ?? 1,
          name: activeSavedView.name,
        },
      }).unwrap();
      billing.claimWorklistSavedViewSubmitted({
        saved_view_id: activeSavedView.billing_saved_view_id,
        action: 'update',
      });
      enqueueSnackbar(`Updated “${activeSavedView.name}” for this organization.`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('This view changed elsewhere. Refresh it before updating.', {
        variant: 'error',
      });
    }
  }, [activeSavedView, billing, currentConfig, enqueueSnackbar, updateSavedView]);

  const deleteCurrentView = useCallback(
    async (view: BillingSavedView) => {
      /* "_requested" is the ask, fired before the outcome — a version
         conflict still counts as the biller trying to delete. */
      billing.claimWorklistSavedViewDeleteRequested({
        saved_view_id: view.billing_saved_view_id,
      });
      try {
        await deleteSavedView({
          expectedVersion: view.version ?? 1,
          savedViewId: view.billing_saved_view_id,
        }).unwrap();
        writeUrlState({ savedViewId: null });
        enqueueSnackbar(`Deleted “${view.name}”.`, { variant: 'success' });
      } catch {
        enqueueSnackbar('This view changed elsewhere. Refresh it before deleting.', {
          variant: 'error',
        });
      }
    },
    [billing, deleteSavedView, enqueueSnackbar, writeUrlState],
  );

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  /* The anchor, resolved against a SETTLED read: flash the row when
     the worked claim is still in view (it returns to the same page via the
     GDN-3196 cursor restore), or say honestly that it left — with the state
     the workbench last knew from work_summary, never a guess.

     `!isFetching` matters: a remount can serve CACHED items while the mount
     refetch is still in flight, and consuming the anchor against that stale
     page could flash a row about to vanish or claim a departure the fresh
     read disproves — the exact dishonesty this anchor exists to avoid. When
     no refetch fires, the cache is within the list's own freshness window
     and is the settled answer. `!error` for the same reason: a FAILED
     refetch settles onto the stale cache — better no anchor than a wrong
     one, and bailing un-consumed lets a successful retry still speak. */
  useEffect(() => {
    if (!anchorArmed || !returnAnchor || returnConsumedRef.current || !data || isFetching || error)
      return;
    returnConsumedRef.current = true;
    /* The history-state strip happens HERE, at consumption — not at mount.
       Stripping earlier would discard an anchor the error guard left
       unconsumed, so a refresh during a failed-fetch window would lose the
       flash/note the eventual successful read should deliver. Consumed →
       stripped → a later refresh doesn't replay; unconsumed → the anchor
       survives the refresh. */
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    const present = items.some(item => item.claim_id === returnAnchor.returnedClaimId);
    if (present) {
      setFlashClaimId(returnAnchor.returnedClaimId);
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      document
        .querySelector(`[data-row-id="${CSS.escape(returnAnchor.returnedClaimId)}"]`)
        ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      /* No cleanup on purpose: a mid-flash refetch re-runs this effect, and
         a cleanup would cancel the fade early. The consumed ref makes the
         timer a one-shot. */
      setTimeout(() => setFlashClaimId(null), 2600);
      return;
    }
    setLeftViewNote(
      returnAnchor.returnedStateLabel
        ? `The claim you worked on is now ${returnAnchor.returnedStateLabel} — it left this view.`
        : 'The claim you worked on is no longer in this view.',
    );
  }, [
    anchorArmed,
    data,
    error,
    isFetching,
    items,
    location.pathname,
    location.search,
    navigate,
    returnAnchor,
  ]);

  const rows = useMemo(
    () =>
      items.map(item => ({
        _raw: item,
        amount: formatCents(item.total_charge_cents ?? 0),
        claim_id: item.claim_id,
        claim_number: item.claim_number ?? '',
        /* Blank, not a dash, when the payer has not answered: an empty cell
           is honest absence, and a placeholder in a numeric column reads as
           a count. */
        days: daysSince(item.last_payer_status?.occurred_at),
        id: item.claim_id,
        issues: item.active_finding_count ?? 0,
        patient_id: item.patient_id,
        patient_name: item.patient_name ?? '—',
        payer_name: item.payer_name ?? '—',
        provider_name: item.clinician_display_name ?? '—',
        service_date: formatDate(item.service_date),
        work_state: item.work_state,
      })),
    [items],
  );

  /* Grouping (GDN-3383) is client-side over the LOADED PAGE, and says so:
     it reorders the rows that already arrived and changes no query
     argument. The runs come back biggest first; inside each one the rows
     keep the order the server sent, which is the view's sort. */
  const groups = useMemo(() => groupClaimRows(rows, groupBy, row => row._raw), [groupBy, rows]);
  const tableRows = useMemo(
    () => (groups.length > 0 ? groups.flatMap(group => group.rows) : rows),
    [groups, rows],
  );
  /* One header per group, keyed to the row it sits above. Built here rather
     than in the table because the label is this queue's language, not a
     table concern. */
  const groupHeaderForRow = useMemo(() => {
    if (groups.length === 0) return undefined;
    const headers = new Map<string, string>();
    for (const group of groups) {
      const leadRow = group.rows[0];
      if (leadRow) headers.set(leadRow.claim_id, claimRowGroupHeader(group));
    }
    return (row: Record<string, unknown>) => headers.get(row.claim_id as string) ?? null;
  }, [groups]);

  const renderCell = useCallback(
    (column: ColumnDef, row: Record<string, unknown>) => {
      const item = row._raw as ClaimWorkQueueItem;
      if (column.key === 'disposition') {
        /* Hidden rows render in FULL, never greyed: a claim someone archived
           is still a claim, and dimming it would say "less real" when the
           chip already says "decided about". */
        return (
          <DispositionChip
            disposition={item.work_disposition}
            lastPayerStatus={item.last_payer_status}
            returnedReason={item.returned_reason}
          />
        );
      }
      if (column.key === 'claim_number') {
        /* The patient control number, which the payer also has, and which
           does not exist before submission (contract v1.1). An internal id
           truncated to look like one would be quoted down a phone line at a
           payer who has never seen it, so the cell says plainly that there
           is nothing to quote yet. */
        if (!item.claim_number) {
          return (
            <Typography color="text.disabled" variant="body2" sx={{ fontSize: '0.8125rem' }}>
              Not assigned
            </Typography>
          );
        }
        return (
          <Typography
            noWrap
            sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 500 }}
          >
            {item.claim_number}
          </Typography>
        );
      }
      if (column.key === 'reason') {
        return (
          <ReasonCell
            item={item}
            content={<ReasonDetailContainer item={item} />}
            onOpen={() => billing.issueDetailOpened({ kind: 'reason', surface: 'claims' })}
          />
        );
      }
      if (column.key === 'payer_reference') {
        /* The payer ICN (`payer_claim_control_number`, contract v1.4): the
           payer's own number for this claim, and the first thing its phone
           system asks for. It is NOT our claim number, so the two live in
           separate columns rather than one column that means different
           things on different rows. */
        const reference = item.payer_claim_control_number?.trim();
        if (reference) {
          return (
            <Typography
              noWrap
              sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 500 }}
            >
              {reference}
            </Typography>
          );
        }
        /* Absent is an answer, not a hole, so the cell says so in words. The
           caption is added only on a REJECTION: a rejected claim was stopped
           at the clearinghouse and never reached the payer, which is exactly
           why no payer number exists. A denial reached the payer, so its
           missing number means something else, and guessing "clearinghouse
           rejection" there would send a biller to the wrong phone number. */
        const clearinghouseRejection = item.last_payer_status?.kind === 'rejected';
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography color="text.secondary" noWrap sx={{ fontSize: '0.8125rem' }}>
              No payer claim #
            </Typography>
            {clearinghouseRejection && (
              <Typography color="text.secondary" noWrap sx={{ display: 'block' }} variant="caption">
                Clearinghouse rejection
              </Typography>
            )}
          </Box>
        );
      }
      if (column.key === 'patient_name') {
        return <PatientIdentity patientId={item.patient_id} patientName={item.patient_name} />;
      }
      if (column.key === 'provider_name') {
        /* The rendering provider — the person who saw the patient — not
           the billing identity this column used to show (GDN-3243). The
           name goes through providerName (NOT badgeName) so the badge's
           initials and hashed color derive from the same person the text
           names. provider_id is that same person server-side
           (models/claims.py, GDN-1926), so id and name agree by
           construction.

           An empty cell is HONEST ABSENCE, not a failure: imported claims
           mostly arrive without one (ADR 0147). It says so in words and
           never falls back to the billing identity. */
        return (
          <ProviderIdentity
            missingLabel="No provider recorded"
            providerId={item.provider_id}
            providerName={item.clinician_display_name}
          />
        );
      }
      /* Rows in the running batch tell their submission status in the issues
         column — the server's per-claim word, live (GDN-3216). Only this
         column: the work-state chip stays the claim's own truth, and it is
         the batch's terminal invalidation, not this override, that updates it. */
      const batchState = batchRowStates.get(item.claim_id);
      if (column.key === 'issues' && batchState) {
        if (batchState.inFlight) {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CircularProgress size={14} />
              <Typography color="text.secondary" variant="caption">
                {batchState.status === 'queued' ? 'Queued' : 'Submitting…'}
              </Typography>
            </Stack>
          );
        }
        if (batchState.attention) {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                {batchState.status === 'blocked' ? 'Blocked before send' : 'Needs attention'}
              </Typography>
            </Stack>
          );
        }
        return (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} />
            <Typography color="text.secondary" variant="caption">
              Sent to clearinghouse
            </Typography>
          </Stack>
        );
      }
      if (column.key === 'issues') {
        /* Real findings render for ANY stage — payer-side content is not
           pre-flight advice. Everything below that is (GDN-3242). */
        const firstFinding = item.finding_highlights?.[0];
        const findingText =
          firstFinding?.why ?? item.submission_top_blocker ?? item.legacy_top_blocker;
        /* Certification copy is pre-flight advice: it renders only while the
           biller can still shape a (re)submission. On in-transit and
           payer-side rows the column says "nothing applies" — a dash, not an
           empty cell that could read as a load failure.
           Deliberately ABOVE the `checking` branch: a certification run in
           flight on a submitted claim cannot change what the payer already
           received, so announcing "Checking claim" there would be the same
           pre-flight noise in spinner form. */
        if (!findingText && !isWorkableClaimStage(item.work_state)) {
          return (
            <Typography
              color="text.disabled"
              data-testid="cert-copy-not-applicable"
              variant="body2"
            >
              —
            </Typography>
          );
        }
        if (item.certification_run_state === 'checking') {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CircularProgress size={14} />
              <Typography variant="body2">Checking claim</Typography>
            </Stack>
          );
        }
        if (!findingText && item.certification_freshness === 'stale') {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ErrorOutlineIcon color="warning" sx={{ fontSize: 16 }} />
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                  Certification is stale
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  Recheck before relying on it
                </Typography>
              </Box>
            </Stack>
          );
        }
        if (!findingText && item.certification_freshness === 'missing') {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <ErrorOutlineIcon color="warning" sx={{ fontSize: 16 }} />
              <Typography color="text.secondary" variant="caption">
                Not checked yet
              </Typography>
            </Stack>
          );
        }
        if (!findingText) {
          return (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16 }} />
              <Typography color="text.secondary" variant="caption">
                Certified · no active findings
              </Typography>
            </Stack>
          );
        }
        const issueCount = firstFinding
          ? `${item.active_finding_count ?? 0} finding${item.active_finding_count === 1 ? '' : 's'}`
          : item.submission_top_blocker
            ? `${item.submission_blocker_count ?? 0} submission issue${item.submission_blocker_count === 1 ? '' : 's'}`
            : `${item.legacy_error_count ?? 0} validation issue${item.legacy_error_count === 1 ? '' : 's'}`;
        /* The cell shows the first finding; the hover detail (GDN-3379) shows
           every one the row and the certification carry. */
        return (
          <HoverDetail
            content={<IssueDetailContainer headline={issueCount} item={item} />}
            onOpen={() => billing.issueDetailOpened({ kind: 'issues', surface: 'claims' })}
          >
            {trigger => (
              <Stack
                direction="row"
                spacing={0.75}
                {...trigger}
                sx={{ minWidth: 0, ...HOVER_DETAIL_TRIGGER_SX }}
              >
                <ErrorOutlineIcon
                  color={item.active_blocker_count ? 'error' : 'warning'}
                  sx={{ flexShrink: 0, fontSize: 16, mt: 0.15 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    {findingText}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {issueCount}
                    {/* The findings branch is exempt from the stage gate — payer
                        content renders at any stage — but this SUFFIX is
                        pre-flight advice riding along with it, so it carries the
                        gate itself. A denial on a with_payer claim can sit beside
                        a stale local certification snapshot; telling the biller
                        to recheck it is the same misleading counsel this ticket
                        removes, just spelled differently (GDN-3242). */}
                    {item.certification_freshness === 'stale' &&
                    isWorkableClaimStage(item.work_state)
                      ? ' · needs recheck'
                      : ''}
                  </Typography>
                </Box>
              </Stack>
            )}
          </HoverDetail>
        );
      }
      /* Claims to fix calls this column Outcome, because on that queue the
         chip is the payer's answer rather than the claim's own state. Same
         chip, same mapping: one truth, two headings. */
      if (column.key === 'work_state' || column.key === 'outcome') {
        /* One reading of the chip for the whole product (GDN-3379): Home,
           the workbench strip and this list all name a denial "Denied", and
           they do it from the same helper rather than three copies of the
           same conditional. */
        return (
          <Chip
            color={WORK_STATE_COLORS[item.work_state] ?? 'default'}
            label={workStateChipLabel(item)}
            size="small"
            variant={workStateChipVariant(item)}
            sx={{ fontWeight: 600 }}
          />
        );
      }
      return null;
    },
    [batchRowStates, billing],
  );

  const selectedItems = items.filter(item => selectedIds.has(item.claim_id));
  const readySelectedIds = selectedItems.filter(item => item.can_submit).map(item => item.claim_id);
  const bulkActions = useMemo<SelectionAction[]>(
    () => [
      {
        disabled: readySelectedIds.length !== selectedIds.size,
        label:
          readySelectedIds.length === selectedIds.size
            ? `Submit ${readySelectedIds.length} claim${readySelectedIds.length === 1 ? '' : 's'}`
            : 'Selection includes blocked claims',
        loading: isBatchSubmitting,
        onClick: async () => {
          /* Fired at the ask — per-claim outcomes land through the shared
             submit event when the batch reaches a terminal state. */
          billing.claimBatchSubmitInitiated({ claim_count: readySelectedIds.length });
          try {
            await submitBatch(readySelectedIds);
            setSelectedIds(new Set());
            enqueueSnackbar(
              `${readySelectedIds.length} claim${readySelectedIds.length === 1 ? '' : 's'} queued for submission.`,
              { variant: 'success' },
            );
          } catch {
            enqueueSnackbar(
              'The batch could not start. The selected claims have not been submitted.',
              {
                variant: 'error',
              },
            );
          }
        },
      },
    ],
    [billing, enqueueSnackbar, isBatchSubmitting, readySelectedIds, selectedIds.size, submitBatch],
  );

  const duplicateName = savedViews.some(
    view => view.name.trim().toLowerCase() === saveName.trim().toLowerCase(),
  );
  const serviceDateField = filterFields.find(field => field.id === 'serviceDate');
  const dateScopeLabel =
    serviceDate && serviceDateField
      ? describeFilter(serviceDateField, serviceDate).value
      : 'All time';
  const isDefaultPredicateSet =
    JSON.stringify(predicates) === JSON.stringify(createDefaultClaimWorkPredicates());
  const currentPage = cursorHistory.length + 1;
  /* Same reason, and the same fallback the rail uses: the last counts
     snapshot this list produced, which is the hidden-excluded total, rather
     than the outgoing page's inflated one or a bare zero under rows that are
     still on screen. */
  const matchingCount = currentData?.counts.matching_count ?? railCounts?.matching_count ?? 0;
  /* What the toggle revealed, counted from the rows on screen rather than a
     server tally: "hidden shown" is a claim about this page, and the page is
     the only thing that can honestly make it.
     Only rows that ARE hidden count. A returned row carries a disposition
     and a chip, but it was on screen before the toggle and is inside the
     queue's own count, so counting it here would claim it twice. The two
     numbers then account for every row: N outstanding plus M revealed. */
  const hiddenShown = items.filter(item => item.hidden).length;
  /* The disposition column is appended, never persisted: it is the toggle's
     explanation of the rows it just revealed, and on a list it is the only
     place a disposition can show at all.
     It also appears UNASKED when a row on the page already carries one. A
     returned row is visible whether or not the toggle is on, and "why is
     this back?" is exactly the question the chip answers; withholding it
     until someone happens to press the toggle would be the one case where
     the column is most needed and least present. */
  const carriesDisposition = items.some(
    item =>
      item.returned_reason ?? (item.work_disposition && item.work_disposition.state !== 'active'),
  );
  const tableColumns = useMemo(
    () =>
      includeHidden || carriesDisposition
        ? [...visibleColumns, DISPOSITION_COLUMN]
        : visibleColumns,
    [carriesDisposition, includeHidden, visibleColumns],
  );

  /* The queue's own count, hidden excluded, so the sentence and the rail
     agree. Falls back to the page's matching count before any count read has
     landed. */
  /* On the first paint of a `hidden=1` link there is no hidden-excluded read
     yet, and the page's own counts include the hidden rows. Nothing is shown
     until the right number arrives: a rail that briefly reads 5 and settles
     to 3 teaches billers not to trust it. */
  const railCountsPending = includeHidden && railCounts === undefined;
  const queueCount = railCounts?.work_view_counts?.[railView] ?? matchingCount;
  const resultStatement = !includeHidden
    ? `${matchingCount} matching claim${matchingCount === 1 ? '' : 's'} · ${dateScopeLabel}`
    : railCountsPending
      ? `${hiddenShown} hidden row${hiddenShown === 1 ? '' : 's'} shown · ${dateScopeLabel}`
      : `${queueCount} ${lowerFirst(claimWorkViewLabelForCount(railView, queueCount))} · ${hiddenShown} hidden row${hiddenShown === 1 ? '' : 's'} shown · ${dateScopeLabel}`;

  /**
   * Straight to the workbench (GDN-3199): the row already carries
   * `encounter_id` (canonical ClaimWorkItem), so routing through the
   * `/jotbill/claim/:id` gate shim — which fetches the claim JUST to learn
   * the encounter id, then replace-redirects — pays a full sequential round
   * trip before the workbench's own 2.35s load even starts. Carrying
   * `claim_id` in the URL also moves the certification and claim-record
   * queries into the FIRST fetch wave. The gate route stays as the fallback
   * door for rows without an encounter id and for external links.
   */
  const { prefetchRow, wasPrefetched } = useWorklistRowPrefetch();

  /* The denominator for the run, which has to agree with the rows in it: the
     hidden-excluded queue count while hidden rows are on screen, the page's
     own matching count otherwise. Null rather than a guess when neither has
     arrived, so the chrome can say "of N loaded" honestly. */
  const queueRunCount = includeHidden
    ? (railCounts?.work_view_counts?.[railView] ?? null)
    : (data?.counts.matching_count ?? null);

  const openClaim = useCallback(
    (item: ClaimWorkQueueItem) => {
      billing.claimWorklistRowClicked({
        claim_id: item.claim_id,
        /* The prefetch's own meter (GDN-3201): clicks on warmed rows vs
           cold ones — paired with workbench_entered latency, the payoff. */
        prefetched: wasPrefetched(item.claim_id),
        route: item.encounter_id ? 'direct' : 'gate_fallback',
      });
      /* Provenance (GDN-3197): the current URL IS the saved list state
         (GDN-3196), so the workbench's back button can say "Claims" and
         return to this exact list. Rides through the gate route too — the
         redirect preserves location.state.

         THE QUEUE (GDN-3322) rides beside it: the rows on screen, in the
         order they are on screen, frozen at this click. Frozen and not
         re-queried on purpose — submitting a claim removes it from "Ready to
         submit", so a live sequence would renumber under the biller and
         change what "next" means between deciding and pressing. Only this
         list captures one; the Encounters worklist and every direct link
         carry provenance alone, exactly as before. */
      const queueLabel = activeSavedView?.name ?? claimWorkViewLabel(railView);
      /* Only rows the workbench can actually HOLD. A claim with no encounter
         routes through the `/jotbill/claim/:id` gate, whose no-encounter branch
         renders the legacy claim view — no rail, no Next — so including one
         would promise a step that strands the biller mid-run. They stay
         clickable as they always were; they are just not part of the sequence,
         and the "of N loaded" qualifier admits the run is smaller than the
         view when any were left out. */
      /* Hidden rows are on screen to be READ, not to be worked through. A run
         that included them would count them in "1 of 5" against a rail that
         says 3, and Next would walk the biller into a claim someone had
         already archived. */
      const queueEntries = items.flatMap(row =>
        row.encounter_id && !row.hidden
          ? [{ claimId: row.claim_id, encounterId: row.encounter_id }]
          : [],
      );
      navigate(
        item.encounter_id
          ? `/jotbill/encounter/${item.encounter_id}?workspace=claim&claim_id=${item.claim_id}`
          : `/jotbill/claim/${item.claim_id}`,
        {
          state: workbenchProvenanceState({
            url: `/jotbill?${searchParams.toString()}`,
            label: 'Claims',
            queue:
              queueLabel && queueEntries.length > 0
                ? {
                    done: false,
                    entries: queueEntries,
                    handled: 0,
                    /* The product's own name for this queue, never invented
                     here: the work view's label, or the saved view's name
                     when one is active. */
                    label: queueLabel,
                    /* Captured whole: the rows loaded here ARE the run. */
                    runId: null,
                    savedViewId: activeSavedView?.billing_saved_view_id ?? null,
                    /* The server's count for THIS list, so the rail can admit
                     when it holds fewer rows than the view does. Null when
                     the read hasn't produced one — no invented denominator. */
                    viewCount: queueRunCount,
                    viewKey: activeSavedView ? null : railView,
                  }
                : null,
          }),
        },
      );
    },
    [
      activeSavedView,
      billing,
      items,
      navigate,
      queueRunCount,
      railView,
      searchParams,
      wasPrefetched,
    ],
  );

  const changePage = (nextPage: number) => {
    restoredCursorRef.current = false;
    if (nextPage === currentPage + 1 && data?.next_cursor) {
      setPageState({
        signature: stateSignature,
        cursor: data.next_cursor,
        history: [...cursorHistory, cursor],
      });
      setSelectedIds(new Set());
      return;
    }
    if (nextPage === currentPage - 1 && cursorHistory.length > 0) {
      const history = [...cursorHistory];
      const previous = history.pop();
      setPageState({ signature: stateSignature, cursor: previous, history });
      setSelectedIds(new Set());
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '220px 1fr' } }}>
        <Skeleton height={340} variant="rounded" />
        <Skeleton height={520} variant="rounded" />
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid="claim-work-list"
        sx={{
          alignItems: 'start',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '220px minmax(0, 1fr)' },
        }}
      >
        <ClaimWorkViewRail
          activeSavedViewId={activeSavedViewId}
          activeWorkView={railView}
          counts={railCounts?.work_view_counts ?? data?.counts.work_view_counts ?? {}}
          countsPending={railCountsPending}
          dirty={dirty}
          isMutating={isSavedViewMutating}
          savedViews={savedViews}
          onDelete={deleteCurrentView}
          onSave={() => setSaveDialogOpen(true)}
          onSelectSavedView={applySavedView}
          onSelectWorkView={selectWorkView}
          onUpdate={updateCurrentView}
        />

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Card variant="outlined">
            <CardContent sx={{ '&:last-child': { pb: 1.5 }, p: 1.5 }}>
              <Stack spacing={1.25}>
                <Typography sx={{ fontWeight: 700 }} variant="subtitle1">
                  {activeSavedView?.name ?? claimWorkViewLabel(railView)}
                </Typography>
                <Divider />
                <FilterBar
                  canClearPredicates={Object.keys(predicates).length > 0}
                  canResetView={activeSavedViewId === null && !isDefaultPredicateSet}
                  fields={filterFields}
                  predicates={predicates}
                  resultStatement={resultStatement}
                  utilityControl={
                    <>
                      {/* Queues only: a list never hides a row, so a control
                          offering to unhide one would be theatre. */}
                      {isQueueView && (
                        <ShowHiddenRowsToggle on={showHidden} onChange={toggleHiddenRows} />
                      )}
                      {/* Beside Sort rather than left of Filters as the
                          mockup draws it: both are "how this list is
                          arranged", and the slot left of Filters belongs to
                          the shared FilterBar. */}
                      {canGroup && (
                        <ClaimGroupMenuButton onChange={changeGrouping} value={groupBy} />
                      )}
                      <SortMenuButton
                        options={SORTS.map(option => ({ label: option.label, value: option.key }))}
                        value={sort}
                        onChange={nextSort => {
                          billing.claimWorklistSortChanged({ sort: nextSort });
                          writeUrlState({ sort: nextSort });
                          resetPage();
                        }}
                      />
                    </>
                  }
                  onClearPredicates={clearPredicates}
                  onPredicatesChange={updatePredicates}
                  onResetView={resetPredicates}
                />
              </Stack>
            </CardContent>
          </Card>

          {error && (
            <Alert
              action={
                <Button color="inherit" onClick={() => refetch()} size="small">
                  Retry
                </Button>
              }
              severity="error"
            >
              Claim work could not load. The canonical work relation may not be available yet.
            </Alert>
          )}

          {leftViewNote && (
            <Alert onClose={() => setLeftViewNote(null)} severity="info">
              {leftViewNote}
            </Alert>
          )}

          <Box sx={{ opacity: isFetching ? 0.72 : 1, transition: 'opacity 120ms ease' }}>
            <DataTable
              columns={tableColumns}
              getRowSelectionLabel={row => `Select claim for ${String(row.patient_name)}`}
              /* Undefined while ungrouped, so the table renders exactly as
                 it did before this ticket. */
              groupHeaderForRow={groupHeaderForRow}
              /* The one action surface (GDN-3216): selection and batch progress
                 live in the table's own header, so nothing floats over rows and
                 the last row is always reachable. */
              headerOverlay={
                selectedIds.size > 0 || batchProgress ? (
                  <ClaimBulkActionSlot
                    actions={bulkActions}
                    batches={batchProgress ? [batchProgress] : []}
                    onClearSelection={() => {
                      billing.claimWorklistClearSelectionClicked({
                        selected_count: selectedIds.size,
                      });
                      setSelectedIds(new Set());
                    }}
                    onDismissBatch={() => {
                      billing.claimBatchDismissClicked({
                        batch_status: batchProgress?.status ?? 'unknown',
                      });
                      dismissBatch();
                    }}
                    onReviewClaim={claimId => {
                      billing.claimBatchReviewClicked({
                        attention_count: [...batchRowStates.values()].filter(s => s.attention)
                          .length,
                        claim_id: claimId,
                      });
                      navigate(`/jotbill/claim/${claimId}`);
                    }}
                    selectedCount={selectedIds.size}
                  />
                ) : undefined
              }
              idKey="claim_id"
              isFetching={isFetching}
              /* isBatchActive, not activeBatchId: a TERMINAL batch keeps its
                 summary on screen but must not keep the worklist locked —
                 selection reopens the moment the server says the run ended. */
              isRowSelectable={row => (row._raw as ClaimWorkQueueItem).can_submit && !isBatchActive}
              onRowClick={row => openClaim(row._raw as ClaimWorkQueueItem)}
              onRowHover={row => {
                const item = row._raw as ClaimWorkQueueItem;
                /* Binding constraint from the selection round (GDN-3216):
                   rows in an ACTIVE batch are mid-submission — a workbench
                   read now would race the drain. Skipping here (not inside
                   the hook) leaves the row eligible again at terminal:
                   nothing was marked as seen. Before the first progress
                   poll lands, membership is UNKNOWN — skip every row for
                   that sub-second window rather than guess. */
                if (
                  isBatchActive &&
                  (batchRowStates.size === 0 || batchRowStates.has(item.claim_id))
                )
                  return;
                prefetchRow(item);
              }}
              onSelectionChange={setSelectedIds}
              renderCell={renderCell}
              /* Queues only: archive, snooze and restore act on a queue
                 place, and a list has none to give back. */
              renderRowEnd={
                isQueueView
                  ? row => {
                      const item = row._raw as ClaimWorkQueueItem;
                      /* No encounter, no menu: the verbs are keyed by
                         encounter, so a claim without one would be handed a
                         kebab whose every item fails. */
                      if (!item.encounter_id) return null;
                      return (
                        <ClaimQueueRowMenu
                          encounterId={item.encounter_id}
                          item={item}
                          onOpenInWorkbench={() => openClaim(item)}
                          serviceDate={String(row.service_date)}
                        />
                      );
                    }
                  : undefined
              }
              rows={tableRows}
              rowSelectionDisabledReason={
                isBatchActive ? 'A submission batch is finishing' : undefined
              }
              stickyHeaderTop={jotBillStickyTop(0)}
              /* The returned-to row flashes (GDN-3197); under
                 prefers-reduced-motion it holds a static tint that clears
                 when the flash window ends. Colors ride the theme's primary,
                 not literals. */
              rowSx={row => {
                const batchState = batchRowStates.get(row.claim_id as string);
                return {
                  /* In-flight batch rows dim; attention rows carry an inset
                     error edge that pairs with the slot's "Review N claims"
                     (GDN-3216). Inset shadow, so row geometry never shifts. */
                  ...(batchState?.inFlight ? { opacity: 0.55 } : {}),
                  ...(batchState?.attention
                    ? { boxShadow: `inset 3px 0 0 ${theme.palette.error.main}` }
                    : {}),
                  ...(row.claim_id === flashClaimId
                    ? {
                        '@keyframes claimWorkRowFlash': {
                          from: { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
                          to: { backgroundColor: 'transparent' },
                        },
                        animation: 'claimWorkRowFlash 2.4s ease-out',
                        '@media (prefers-reduced-motion: reduce)': {
                          animation: 'none',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }
                    : {}),
                };
              }}
              selectedIds={selectedIds}
            />
          </Box>

          <Pagination
            data={{
              page: currentPage,
              per_page: perPage,
              total: matchingCount,
              total_pages: Math.max(1, Math.ceil(matchingCount / perPage)),
            }}
            onPageChange={changePage}
            onPerPageChange={nextPerPage => {
              writeUrlState({ perPage: nextPerPage });
              resetPage();
            }}
          />
        </Stack>
      </Box>

      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setSaveDialogOpen(false)}
        open={saveDialogOpen}
      >
        <DialogTitle>Save organization view</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              error={duplicateName}
              helperText={
                duplicateName
                  ? 'A saved view already uses this name.'
                  : 'Everyone in this organization will see this view.'
              }
              label="View name"
              onChange={event => setSaveName(event.target.value)}
              value={saveName}
            />
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip label={claimWorkViewLabel(railView)} size="small" />
              <Chip label={SORTS.find(option => option.key === sort)?.label} size="small" />
              <Chip label={`${Object.keys(predicates).length} filters`} size="small" />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            disabled={!saveName.trim() || duplicateName || createState.isLoading}
            onClick={saveCurrentView}
            startIcon={
              createState.isLoading ? <CircularProgress size={14} /> : <SaveOutlinedIcon />
            }
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            Save view
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
